import { useMemo, useState, useEffect, useRef } from 'react';
import useIsUSD from '@/hooks/useIsUSD';
import { useLocation, useNavigate } from 'react-router-dom';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import DoneIcon from '@mui/icons-material/Done';
import LanguageIcon from '@mui/icons-material/Language';
import { useOnboardingState } from '../../hooks/useOnboardingState.jsx';
import { MONTHLY_PLANS, YEARLY_PLANS } from '@/data/utils/plansData';
import { onboardingV2Api } from '@/data/api/onboardingV2';
import { useSetAtom } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import PayPalIcon from '@/Asset/images/icon/paypal.svg';
import Logo from '@/Asset/images/logo-blaxk.webp';
import Toast from '@/utils/toastManager';
import EastIcon from '@mui/icons-material/East';
import axiosConfig from '@/data/utils/axiosConfigInstance';
import { trackPaymentUiClick } from '@/data/utils/paymentUiAnalytics';
import { getAllTrackingIds, persistMetaCookiesToStorage } from '@/utils/metaCookies';
import './Checkout.css';

const COUNTRIES = [
	{ code: 'US', name: 'USA' },
	{ code: 'CA', name: 'Canada' },
	{ code: 'GB', name: 'UK' },
	{ code: 'AU', name: 'Australia' },
	{ code: 'NZ', name: 'New Zealand' },
	{ code: 'IN', name: 'India' },
	{ code: 'AF', name: 'Afghanistan' },
	{ code: 'AL', name: 'Albania' },
	{ code: 'DZ', name: 'Algeria' },
	{ code: 'AR', name: 'Argentina' },
	{ code: 'AT', name: 'Austria' },
	{ code: 'BE', name: 'Belgium' },
	{ code: 'BR', name: 'Brazil' },
	{ code: 'CN', name: 'China' },
	{ code: 'DK', name: 'Denmark' },
	{ code: 'EG', name: 'Egypt' },
	{ code: 'FI', name: 'Finland' },
	{ code: 'FR', name: 'France' },
	{ code: 'DE', name: 'Germany' },
	{ code: 'GR', name: 'Greece' },
	{ code: 'HK', name: 'Hong Kong' },
	{ code: 'IE', name: 'Ireland' },
	{ code: 'IL', name: 'Israel' },
	{ code: 'IT', name: 'Italy' },
	{ code: 'JP', name: 'Japan' },
	{ code: 'MX', name: 'Mexico' },
	{ code: 'NL', name: 'Netherlands' },
	{ code: 'NO', name: 'Norway' },
	{ code: 'PL', name: 'Poland' },
	{ code: 'PT', name: 'Portugal' },
	{ code: 'RU', name: 'Russia' },
	{ code: 'SA', name: 'Saudi Arabia' },
	{ code: 'SG', name: 'Singapore' },
	{ code: 'ZA', name: 'South Africa' },
	{ code: 'KR', name: 'South Korea' },
	{ code: 'ES', name: 'Spain' },
	{ code: 'SE', name: 'Sweden' },
	{ code: 'CH', name: 'Switzerland' },
	{ code: 'TH', name: 'Thailand' },
	{ code: 'TR', name: 'Turkey' },
	{ code: 'AE', name: 'UAE' }
];

const parseBillingCycle = value => {
	const billingCycleText = String(value || '').toLowerCase();
	if (billingCycleText === 'monthly' || billingCycleText === 'mothly') return 'monthly';
	if (billingCycleText === 'yearly' || billingCycleText === 'annual' || billingCycleText === 'annually') return 'yearly';
	return null;
};

const getCardType = cardNumber => {
	const cleanedCardNumber = String(cardNumber || '').replace(/\D/g, '');
	if (/^3[47]/.test(cleanedCardNumber)) {
		return 'amex';
	}
	return 'other';
};

const formatCardNumberInput = cardNumber => {
	const cleanedCardNumber = String(cardNumber || '').replace(/\D/g, '');

	if (/^3[47]/.test(cleanedCardNumber)) {
		const trimmedCardNumber = cleanedCardNumber.slice(0, 15);
		const parts = [];

		if (trimmedCardNumber.length > 0) {
			parts.push(trimmedCardNumber.slice(0, 4));
		}
		if (trimmedCardNumber.length > 4) {
			parts.push(trimmedCardNumber.slice(4, 10));
		}
		if (trimmedCardNumber.length > 10) {
			parts.push(trimmedCardNumber.slice(10, 15));
		}

		return parts.join(' ');
	}

	const trimmedCardNumber = cleanedCardNumber.slice(0, 19);
	return trimmedCardNumber.match(/.{1,4}/g)?.join(' ') || '';
};

const isLuhnValid = cardNumber => {
	const digits = String(cardNumber || '').replace(/\D/g, '');
	if (!digits) return false;

	let sum = 0;
	let shouldDouble = false;

	for (let index = digits.length - 1; index >= 0; index -= 1) {
		let digit = Number(digits[index]);

		if (shouldDouble) {
			digit *= 2;
			if (digit > 9) {
				digit -= 9;
			}
		}

		sum += digit;
		shouldDouble = !shouldDouble;
	}

	return sum % 10 === 0;
};

const isCardNumberValid = (cardNumber, cardType) => {
	const cleanedCardNumber = String(cardNumber || '').replace(/\D/g, '');

	if (cardType === 'amex') {
		return /^3[47]\d{13}$/.test(cleanedCardNumber) && isLuhnValid(cleanedCardNumber);
	}

	return /^\d{13,19}$/.test(cleanedCardNumber) && isLuhnValid(cleanedCardNumber);
};

const formatPlanLabel = value =>
	String(value || '')
		.trim()
		.split(/[\s-_]+/)
		.filter(Boolean)
		.map(token => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
		.join(' ');

const parseCheckoutQuery = search => {
	const params = new URLSearchParams(search);
	const planId = params.get('planId');
	const planType = params.get('plan');
	const billingFromParams = params.get('billing');
	const coupon = params.get('coupon');
	const forceCheckout = params.get('forceCheckout');

	if (planId || planType || billingFromParams || coupon || forceCheckout) {
		return {
			planId,
			planType,
			billingCycle: parseBillingCycle(billingFromParams),
			coupon,
			forceCheckout: forceCheckout === '1' || String(forceCheckout || '').toLowerCase() === 'true'
		};
	}

	// Supports shorthand query like: /onboarding/checkout?standard?mothly
	const raw = String(search || '')
		.replace(/^\?/, '')
		.trim();
	if (!raw) return { planId: null, planType: null, billingCycle: null };
	const [rawPlanType, rawBilling] = raw.split('?');

	return {
		planId: null,
		planType: rawPlanType || null,
		billingCycle: parseBillingCycle(rawBilling),
		coupon: null,
		forceCheckout: false
	};
};

const mapPlanIdToSeatId = (planId, billingCycle) => {
	if (!planId) return null;
	
	const isMonthly = billingCycle === 'monthly';
	
	if (planId === 'INDIVIDUAL-Monthly' || planId === 'INDIVIDUAL-Yearly') {
		return planId;
	}
	
	if (planId === 'VIP-Monthly') return 'VIP-Monthly';
	if (planId === 'VIP-Yearly') return 'VIP-Yearly';
	
	if (planId === 'STUDIO-Monthly') return 'Studio-Account-5';
	if (planId === 'STUDIO-Yearly') return 'Studio-Account-5';
	
	return planId;
};

const resolveSelectedPlan = (selectedPlanId, selectedBillingCycle) => {
	const plans = selectedBillingCycle === 'monthly' ? MONTHLY_PLANS : YEARLY_PLANS;

	for (const plan of plans) {
		if (plan.hasSeatSelection) {
			const seat = plan.seats.find(item => item.seatId === selectedPlanId);
			if (seat) {
				return {
					plan,
					activePlan: seat,
					title: `${plan.title} ${seat.seatLabel}`
				};
			}
		} else if (plan.id === selectedPlanId) {
			return {
				plan,
				activePlan: plan,
				title: plan.title
			};
		}
	}

	const fallback = YEARLY_PLANS[1];
	return {
		plan: fallback,
		activePlan: fallback,
		title: fallback.title
	};
};

const Checkout = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { state, updateState } = useOnboardingState();
	const setUserData = useSetAtom(userInfoAtom);
	const [loading, setLoading] = useState(false);
	const [paypalLoading, setPaypalLoading] = useState(true);
	const [applePayAvailable, setApplePayAvailable] = useState(false);
	const paypalInitialized = useRef(false);
	const paypalSubscriptionInProgress = useRef(false);
	const applePayContainerRef = useRef(null);
	const applePayFlowRef = useRef({
		chargebee: null,
		applePayHandler: null,
		paymentIntent: null,
		traceId: null,
		handlerAttached: false
	});
	const [paymentData, setPaymentData] = useState({
		cardholderName: '',
		cardNumber: '',
		cardExpiry: '',
		cardCvv: '',
		zipCode: '',
		country: 'US'
	});
	const [errors, setErrors] = useState({});
	const [couponCode, setCouponCode] = useState('');
	const [couponStatus, setCouponStatus] = useState('');
	const [priceEstimate, setPriceEstimate] = useState(null);
	const [showCouponInput, setShowCouponInput] = useState(false);
	const querySelection = useMemo(() => parseCheckoutQuery(location.search), [location.search]);
	const autoCouponAppliedRef = useRef(false);

	useEffect(() => {
    if (window.paypal) {
      window.paypal.Buttons({
        style: {
          layout: "horizontal",
          height: 46,   // 👈 PayPal button height
          shape: "rect",
          label: "paypal"
        }
      }).render("#paypal-container");
    }
  }, []);

	const showApplePay = useMemo(() => {
		const ua = navigator.userAgent;
		const isAppleDevice = /iPad|iPhone|iPod|Macintosh/.test(ua);
		const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|Edg/.test(ua);
		return isAppleDevice && isSafari;
	}, []);

	const selectedBillingCycle = querySelection.billingCycle || state.selectedBillingCycle || 'yearly';
	const rawPlanId = querySelection.planId || state.selectedPlanId;
	const selectedPlanId = mapPlanIdToSeatId(rawPlanId, selectedBillingCycle) || rawPlanId;
	const { plan, activePlan, title } = useMemo(
		() => resolveSelectedPlan(selectedPlanId, selectedBillingCycle),
		[selectedPlanId, selectedBillingCycle]
	);

	const displayTitle = title;

	const totalPrice = Number(activePlan.price).toFixed(2);
	const displayPrice = priceEstimate ? priceEstimate.total.toFixed(2) : totalPrice;
	const discountAmount = priceEstimate ? priceEstimate.discount.toFixed(2) : 0;
	const { isUSD } = useIsUSD();
	const PlanIcon = plan.icon;
	const isTrialPlan = !String(plan?.id || '').toLowerCase().includes('studio');
	const detectedCardType = useMemo(() => getCardType(paymentData.cardNumber), [paymentData.cardNumber]);
	const trackCheckoutPaymentEvent = (eventName, paymentMethod, meta = {}, traceIdOverride = null) => {
		const planId = state.selectedPlanCbItem || selectedPlanId;
		const resolvedTraceId =
			traceIdOverride !== null && traceIdOverride !== undefined
				? traceIdOverride
				: paymentMethod === 'card'
				? null
				: applePayFlowRef.current.traceId || null;
		void trackPaymentUiClick({
			eventName,
			eventAction: 'click',
			paymentMethod,
			planId,
			billingCycle: selectedBillingCycle,
			traceId: resolvedTraceId,
			source: 'onboarding_checkout',
			meta
		});
	};

	// // Initialize Apple Pay with Chargebee.js Apple Pay handler
	// useEffect(() => {
	// 	const initApplePay = async () => {
	// 		console.log('[ApplePay] Init started, showApplePay:', showApplePay);
	// 		if (!showApplePay) {
	// 			setApplePayAvailable(false);
	// 			trackCheckoutPaymentEvent('apple_pay_unavailable', 'apple_pay', { reason: 'unsupported_device_or_browser' });
	// 			return;
	// 		}

	// 		try {
	// 			const userData = JSON.parse(localStorage.getItem('userData') || '{}');
	// 			if (!userData.email) {
	// 				setApplePayAvailable(false);
	// 				trackCheckoutPaymentEvent('apple_pay_unavailable', 'apple_pay', { reason: 'missing_user_email' });
	// 				return;
	// 			}

	// 			if (!window.Chargebee) {
	// 				const script = document.createElement('script');
	// 				script.src = 'https://js.chargebee.com/v2/chargebee.js';
	// 				script.async = true;
	// 				document.body.appendChild(script);
	// 				await new Promise(resolve => (script.onload = resolve));
	// 			}

	// 			const cb = window.Chargebee.init({
	// 				site: import.meta.env.VITE_CHARGEBEE_SITE,
	// 				publishableKey: import.meta.env.VITE_APP_CHARGEBEE_API_KEY
	// 			});

	// 			const response = await axiosConfig.post(
	// 				`${import.meta.env.VITE_APP_API_URL}/chargebee/create-payment-intent`,
	// 				{
	// 					amount: Math.round(Number(activePlan.price) * 100),
	// 					currency_code: 'USD',
	// 					payment_method_type: 'apple_pay',
	// 					planId: state.selectedPlanCbItem || selectedPlanId,
	// 					isTrialPlan,
	// 					email: userData.email,
	// 					firstName: userData.firstName || '',
	// 					lastName: userData.lastName || ''
	// 				}
	// 			);

	// 			const paymentIntent = response.data?.payment_intent;
	// 			const flowTraceId = response.data?.traceId || paymentIntent?.id || null;
	// 			if (!paymentIntent?.id) {
	// 				throw new Error('Chargebee payment intent not returned for Apple Pay');
	// 			}

	// 			const applePayHandler = await cb.load('apple-pay');
	// 			applePayHandler.setPaymentIntent(paymentIntent);

	// 			let applePayAvailableOnDevice = true;
	// 			try {
	// 				if (typeof applePayHandler.canMakePayments === 'function') {
	// 					const canMakePayments = await applePayHandler.canMakePayments();
	// 					applePayAvailableOnDevice = canMakePayments !== false;
	// 				}
	// 			} catch (capabilityError) {
	// 				console.warn('[ApplePay] Capability check warning:', capabilityError?.message || capabilityError);
	// 			}

	// 			if (!applePayAvailableOnDevice) {
	// 				setApplePayAvailable(false);
	// 				trackCheckoutPaymentEvent(
	// 					'apple_pay_unavailable',
	// 					'apple_pay',
	// 					{ reason: 'can_make_payments_false' },
	// 					flowTraceId
	// 				);
	// 				return;
	// 			}

	// 			applePayFlowRef.current.chargebee = cb;
	// 			applePayFlowRef.current.applePayHandler = applePayHandler;
	// 			applePayFlowRef.current.paymentIntent = paymentIntent;
	// 			applePayFlowRef.current.traceId = flowTraceId;
	// 			applePayFlowRef.current.handlerAttached = false;
	// 			setApplePayAvailable(true);

	// 			trackCheckoutPaymentEvent(
	// 				'apple_pay_available',
	// 				'apple_pay',
	// 				{ intentType: 'chargebee_payment_intent', paymentIntentId: paymentIntent.id },
	// 				flowTraceId
	// 			);
	// 		} catch (error) {
	// 			console.error('[ApplePay] Error during initialization:', error);
	// 			setApplePayAvailable(false);
	// 			trackCheckoutPaymentEvent(
	// 				'apple_pay_init_failed',
	// 				'apple_pay',
	// 				{ error: error?.response?.data?.error || error.message },
	// 				applePayFlowRef.current.traceId
	// 			);
	// 		}
	// 	};

	// 	initApplePay();
	// }, [showApplePay, activePlan.price, isTrialPlan, state.selectedPlanCbItem, selectedPlanId]);

	// // Mount Apple Pay button and handle Chargebee authorization flow
	// useEffect(() => {
	// 	const applePayFlow = applePayFlowRef.current;
	// 	if (!applePayAvailable || !applePayContainerRef.current || !applePayFlow.applePayHandler) return;
	// 	if (applePayFlow.handlerAttached) return;

	// 	const applePayHandler = applePayFlow.applePayHandler;
	// 	const flowTraceId = applePayFlow.traceId || applePayFlow.paymentIntent?.id || null;
	// 	let isUnmounted = false;

	// 	const handleApplePayButtonClick = () => {
	// 		trackCheckoutPaymentEvent(
	// 			'apple_pay_button_clicked',
	// 			'apple_pay',
	// 			{ intentType: 'chargebee_payment_intent' },
	// 			flowTraceId
	// 		);
	// 	};

	// 	const mountAndHandle = async () => {
	// 		try {
	// 			await applePayHandler.mountPaymentButton('#apple-pay-button-container');
	// 			if (applePayContainerRef.current) {
	// 				applePayContainerRef.current.addEventListener('click', handleApplePayButtonClick);
	// 			}
	// 			applePayFlow.handlerAttached = true;

	// 			const authorizedIntent = await applePayHandler.handlePayment();
	// 			if (isUnmounted) return;

	// 			if (!authorizedIntent || authorizedIntent.status !== 'authorized') {
	// 				trackCheckoutPaymentEvent(
	// 					'apple_pay_payment_not_authorized',
	// 					'apple_pay',
	// 					{ status: authorizedIntent?.status || 'unknown' },
	// 					flowTraceId
	// 				);
	// 				Toast.error('Apple Pay authorization was not completed.');
	// 				return;
	// 			}

	// 			trackCheckoutPaymentEvent(
	// 				'apple_pay_payment_authorized',
	// 				'apple_pay',
	// 				{ paymentIntentId: authorizedIntent.id },
	// 				flowTraceId
	// 			);

	// 			const userData = JSON.parse(localStorage.getItem('userData') || '{}');
	// 			const subscriptionPayload = {
	// 				planId: state.selectedPlanCbItem || selectedPlanId,
	// 				email: userData.email,
	// 				applePayEmail: userData.email,
	// 				firstName: userData.firstName || '',
	// 				lastName: userData.lastName || '',
	// 				traceId: flowTraceId || authorizedIntent.id,
	// 				payment_method_type: 'apple_pay',
	// 				paymentIntentId: authorizedIntent.id,
	// 				couponId: couponStatus === 'valid' ? couponCode.trim() : undefined
	// 			};

	// 			const subscriptionResponse = await axiosConfig.post(
	// 				`${import.meta.env.VITE_APP_API_URL}/chargebee/create-subscription-with-payment-intent`,
	// 				subscriptionPayload
	// 			);

	// 			const onboardingPreferences = { ...(state.onboardingPreferences || {}) };
	// 			try {
	// 				await onboardingV2Api.updateOnboardingData({ onboardingStep: 3, onboardingPreferences });
	// 			} catch (err) {
	// 				console.error('Failed to update onboarding data:', err);
	// 			}

	// 			const updatedState = { ...state, checkoutStatus: 'completed', currentStep: 3, onboardingPreferences };
	// 			sessionStorage.setItem('onboarding_state', JSON.stringify(updatedState));
	// 			updateState({ checkoutStatus: 'completed', currentStep: 3, onboardingPreferences });

	// 			if (subscriptionResponse.data?.userType) {
	// 				const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}');
	// 				const updatedUserData = {
	// 					...currentUserData,
	// 					userType: subscriptionResponse.data.userType,
	// 					isTrialAvailed: true,
	// 					requiredSeats: subscriptionResponse.data.requiredSeats,
	// 					availableSeats: subscriptionResponse.data.availableSeats
	// 				};
	// 				localStorage.setItem('userData', JSON.stringify(updatedUserData));
	// 				setUserData(prev => ({ ...prev, user: updatedUserData, userType: subscriptionResponse.data.userType }));
	// 			}

	// 			trackCheckoutPaymentEvent(
	// 				'apple_pay_subscription_success',
	// 				'apple_pay',
	// 				{ subscriptionId: subscriptionResponse.data?.subscription?.id },
	// 				flowTraceId
	// 			);

	// 			Toast.applePay('Subscription activated!');
	// 			const params = new URLSearchParams({
	// 				...Object.fromEntries(new URLSearchParams(location.search || '').entries()),
	// 				plan: state.selectedPlanType || querySelection.planType || 'standard',
	// 				billing: selectedBillingCycle,
	// 				planId: selectedPlanId
	// 			});
	// 			navigate(`/onboarding/welcome?${params.toString()}`);
	// 		} catch (error) {
	// 			if (isUnmounted) return;
	// 			console.error('[ApplePay] Flow error:', error);
	// 			trackCheckoutPaymentEvent(
	// 				'apple_pay_subscription_failed',
	// 				'apple_pay',
	// 				{ error: error.response?.data?.error || error.message },
	// 				flowTraceId
	// 			);
	// 			const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Apple Pay failed. Please try again.';
	// 			Toast.error(errorMsg);
	// 		}
	// 	};

	// 	mountAndHandle();

	// 	return () => {
	// 		isUnmounted = true;
	// 		applePayFlow.handlerAttached = false;
	// 		if (applePayContainerRef.current) {
	// 			applePayContainerRef.current.removeEventListener('click', handleApplePayButtonClick);
	// 			applePayContainerRef.current.innerHTML = '';
	// 		}
	// 	};
	// }, [applePayAvailable, selectedBillingCycle, selectedPlanId, state.selectedPlanCbItem, state.selectedPlanType, querySelection.planType]);



	// Initialize PayPal
	useEffect(() => {
		// Redirect if checkout already completed
		if (state.checkoutStatus === 'completed' && !querySelection?.forceCheckout) {
			console.log('[PayPal] Checkout already completed, redirecting to welcome');
			const params = new URLSearchParams({
				...Object.fromEntries(new URLSearchParams(location.search || '').entries()),
				plan: state.selectedPlanType || querySelection.planType || 'standard',
				billing: selectedBillingCycle,
				planId: selectedPlanId
			});
			navigate(`/onboarding/welcome?${params.toString()}`, { replace: true });
			return;
		}

		console.log('[PayPal] useEffect triggered, paypalInitialized:', paypalInitialized.current);

		// Skip if already initialized
		if (paypalInitialized.current) {
			console.log('[PayPal] Already initialized, skipping');
			return;
		}

		const initPaypal = async () => {
			try {
				console.log('[PayPal] Starting initialization');
				const userData = JSON.parse(localStorage.getItem('userData') || '{}');
				if (!userData.email) {
					console.log('[PayPal] No user email, aborting');
					return;
				}

				paypalInitialized.current = true;
				console.log('[PayPal] Set initialized flag to true');

				if (!window.Chargebee) {
					console.log('[PayPal] Loading Chargebee SDK');
					const script = document.createElement('script');
					script.src = 'https://js.chargebee.com/v2/chargebee.js';
					script.async = true;
					document.body.appendChild(script);
					await new Promise(resolve => (script.onload = resolve));
					console.log('[PayPal] Chargebee SDK loaded');
				}

				const cb = window.Chargebee.init({
					site: import.meta.env.VITE_CHARGEBEE_SITE,
					publishableKey: import.meta.env.VITE_APP_CHARGEBEE_API_KEY
				});
				console.log('[PayPal] Chargebee initialized');

				console.log('[PayPal] Creating payment intent');
				const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/paypal-payment-intent`, {
					amount: Math.round(Number(activePlan.price) * 100),
					currency_code: 'USD',
					email: userData.email,
					firstName: userData.firstname || userData.firstName || '',
					lastName: userData.lastname || userData.lastName || ''
				});

				const paymentIntent = response.data.payment_intent;
				const paypalTraceId = response.data.traceId || paymentIntent.id;
				console.log('[PayPal] Payment intent created:', paymentIntent.id);

				const paypalHandler = await cb.load('paypal');
				paypalHandler.setPaymentIntent(paymentIntent);
				console.log('[PayPal] PayPal handler loaded');

				await paypalHandler.mountPaymentButton('#paypal-container', {
					style: {
						layout: 'vertical',
						color: 'white',
						shape: 'rect',
						label: 'paypal',
						size: 'responsive'
					}
				});
				console.log('[PayPal] Button mounted, waiting for user interaction');
				setPaypalLoading(false);
				const paypalContainer = document.getElementById('paypal-container');
				const handlePaypalButtonClick = event => {
					if (paypalSubscriptionInProgress.current) {
						event.preventDefault();
						event.stopPropagation();
						return;
					}
					trackCheckoutPaymentEvent('paypal_button_clicked', 'paypal', {
						paymentIntentId: paymentIntent.id
					}, paypalTraceId);
				};
				if (paypalContainer) {
					paypalContainer.addEventListener('click', handlePaypalButtonClick);
				}

				paypalHandler
					.handlePayment()
					.then(async authorizedIntent => {
						console.log('[PayPal] handlePayment resolved:', authorizedIntent);

						if (authorizedIntent?.status === 'authorized') {
							if (paypalSubscriptionInProgress.current) {
								trackCheckoutPaymentEvent('paypal_duplicate_authorization_ignored', 'paypal', {
									paymentIntentId: authorizedIntent.id
								}, paypalTraceId);
								return;
							}

							paypalSubscriptionInProgress.current = true;
							setPaypalLoading(true);
							console.log('[PayPal] Payment authorized, creating subscription');
							trackCheckoutPaymentEvent('paypal_payment_authorized', 'paypal', {
								paymentIntentId: authorizedIntent.id
							}, paypalTraceId);

							persistMetaCookiesToStorage();
							const subResponse = await axiosConfig.post(
								`${import.meta.env.VITE_APP_API_URL}/chargebee/paypal-create-subscription`,
								{
									email: userData.email,
									firstName: userData.firstname || userData.firstName || '',
									lastName: userData.lastname || userData.lastName || '',
									planId: state.selectedPlanCbItem || selectedPlanId,
									paymentIntentId: authorizedIntent.id,
									traceId: paypalTraceId,
									couponId: sessionStorage.getItem('appliedCouponCode') || undefined,
									...getAllTrackingIds()
								}
							);

							console.log('[PayPal] Subscription response:', subResponse.data);

							if (subResponse.data.success) {
								console.log('[PayPal] Subscription successful, persisting state and redirecting');
								trackCheckoutPaymentEvent('paypal_subscription_success', 'paypal', {
									paymentIntentId: authorizedIntent.id
								}, paypalTraceId);

								// Force persist state to sessionStorage immediately
								const updatedState = {
									...state,
									checkoutStatus: 'completed',
									currentStep: 3
								};
								sessionStorage.setItem('onboarding_state', JSON.stringify(updatedState));

								// Also update via hook
								updateState({
									checkoutStatus: 'completed',
									currentStep: 3
								});

								Toast.success('Subscription activated!');

								const params = new URLSearchParams({
									plan: state.selectedPlanType || querySelection.planType || 'standard',
									billing: selectedBillingCycle,
									planId: selectedPlanId
								});

								const welcomeUrl = `/onboarding/welcome?${params.toString()}`;
								console.log('[PayPal] Redirecting to:', welcomeUrl);

								window.location.href = welcomeUrl;
							} else {
								console.log('[PayPal] Subscription failed');
								trackCheckoutPaymentEvent('paypal_subscription_failed', 'paypal', {
									paymentIntentId: authorizedIntent.id
								}, paypalTraceId);
								paypalSubscriptionInProgress.current = false;
								setPaypalLoading(false);
							}
						} else {
							console.log('[PayPal] Payment not authorized, status:', authorizedIntent?.status);
							trackCheckoutPaymentEvent('paypal_payment_not_authorized', 'paypal', {
								status: authorizedIntent?.status
							}, paypalTraceId);
						}
					})
					.catch(error => {
						console.error('[PayPal] handlePayment error:', error);
						trackCheckoutPaymentEvent('paypal_flow_failed', 'paypal', {
							error: error.message
						}, paypalTraceId);
						paypalSubscriptionInProgress.current = false;
						setPaypalLoading(false);
					});
			} catch (error) {
				console.error('[PayPal] Init error:', error);
				trackCheckoutPaymentEvent('paypal_init_failed', 'paypal', {
					error: error.message
				}, null);
				paypalInitialized.current = false;
			}
		};

		initPaypal();
	}, []);

	const handleInputChange = (field, value) => {
		// Format card number with spaces
		if (field === 'cardNumber') {
			const formattedCardNumber = formatCardNumberInput(value);
			const currentCardType = getCardType(formattedCardNumber);
			const maxCvvLength = currentCardType === 'amex' ? 4 : 3;

			setPaymentData(prev => ({
				...prev,
				cardNumber: formattedCardNumber,
				cardCvv: String(prev.cardCvv || '')
					.replace(/\D/g, '')
					.slice(0, maxCvvLength)
			}));
			setErrors(prev => ({ ...prev, cardNumber: '', cardCvv: '' }));
			return;
		}

		// Format expiry as MM/YY
		if (field === 'cardExpiry') {
			value = value.replace(/\D/g, '');
			if (value.length >= 2) {
				value = value.slice(0, 2) + '/' + value.slice(2, 4);
			}
			if (value.length > 5) return;
		}

		// CVV - only numbers, max 4
		if (field === 'cardCvv') {
			const currentCardType = getCardType(paymentData.cardNumber);
			const maxCvvLength = currentCardType === 'amex' ? 4 : 3;
			value = value.replace(/\D/g, '').slice(0, maxCvvLength);
		}

		// Zip - allow alphanumeric for international zip codes
		if (field === 'zipCode') {
			value = value.slice(0, 10);
			// Auto-detect country from zip code
			const detectedCountry = detectCountryFromZip(value);
			setPaymentData(prev => ({ ...prev, zipCode: value, country: detectedCountry }));
			setErrors(prev => ({ ...prev, zipCode: '', country: '' }));
			return;
		}

		setPaymentData(prev => ({ ...prev, [field]: value }));
		setErrors(prev => ({ ...prev, [field]: '' }));
	};

	const detectCountryFromZip = zip => {
		if (!zip || !zip.trim()) return 'US';
		const cleanedZip = zip.trim().toUpperCase();

		// Indian PIN codes: 6 digits
		if (/^\d{6}$/.test(cleanedZip)) return 'IN';

		// UK postcodes: alphanumeric, various formats (check before 4-digit patterns)
		if (/^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i.test(cleanedZip)) return 'GB';

		// Canadian postal codes: letter-digit-letter space digit-letter-digit
		if (/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(cleanedZip)) return 'CA';

		// Australian postcodes: 4 digits (check after UK/Canada)
		if (/^\d{4}$/.test(cleanedZip)) return 'AU';

		// US: 5 digits or 5+4 format
		if (/^\d{5}(-?\d{4})?$/.test(cleanedZip)) return 'US';

		// Default to US
		return 'US';
	};

	// Validate zip code based on detected country
	const validateZipCode = zip => {
		if (!zip || !zip.trim()) return false;

		const country = detectCountryFromZip(zip);

		const patterns = {
			US: /^\d{5}(-\d{4})?$/, // US: 5 digits or 5+4
			AU: /^\d{4}$/, // Australia: 4 digits
			CA: /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i, // Canada: postal code format
			GB: /^[A-Z]{1,2}\d{1,2}[A-Z]? ?\d[A-Z]{2}$/i, // UK
			NZ: /^\d{4}$/, // New Zealand: 4 digits
			IN: /^\d{6}$/ // India: 6 digits
		};

		// For unknown countries, allow alphanumeric up to 10 chars
		if (!patterns[country]) {
			return zip.length >= 3 && zip.length <= 10;
		}

		return patterns[country].test(zip.trim());
	};

	const extractCouponErrorMessage = (error, fallback = 'Failed to validate coupon') => {
		const rawMessage =
			error?.response?.data?.message ||
			error?.response?.data?.error ||
			error?.message ||
			fallback;

		return String(rawMessage).replace(/^subscription\[coupon\]\s*:\s*/i, '').trim();
	};

	const handleApplyCoupon = async (codeOverride = null) => {
		if (codeOverride && typeof codeOverride === 'object' && typeof codeOverride.preventDefault === 'function') {
			codeOverride.preventDefault();
		}

		const codeInput =
			typeof codeOverride === 'string'
				? codeOverride
				: typeof couponCode === 'string'
				? couponCode
				: String(couponCode ?? '');
		const code = codeInput.trim();
		if (!code) return Toast.error('Please enter a coupon code');
		setCouponStatus('validating');
		try {
			const validateRes = await onboardingV2Api.validateCoupon(code, state.selectedPlanCbItem || selectedPlanId);
			if (!validateRes.data.valid) {
				setCouponStatus('invalid');
				sessionStorage.removeItem('appliedCouponCode');
				const errorMessage = String(validateRes.data.message || 'Invalid coupon')
					.replace(/^subscription\[coupon\]\s*:\s*/i, '')
					.trim();
				return Toast.error(errorMessage);
			}
			const estimateRes = await onboardingV2Api.estimatePrice(state.selectedPlanCbItem || selectedPlanId, code);
			if (estimateRes.data.success) {
				setCouponStatus('valid');
				setCouponCode(code);
				setPriceEstimate(estimateRes.data.estimate);
				sessionStorage.setItem('appliedCouponCode', code);
				Toast.success('Coupon applied!');
			} else {
				setCouponStatus('invalid');
				sessionStorage.removeItem('appliedCouponCode');
				Toast.error(estimateRes.data.message || estimateRes.data.error || 'Failed to calculate discount');
			}
		} catch (error) {
			setCouponStatus('invalid');
			sessionStorage.removeItem('appliedCouponCode');
			const errorMessage = extractCouponErrorMessage(error);
			Toast.error(errorMessage);
		}
	};

	useEffect(() => {
		const couponFromQuery = String(querySelection?.coupon || '').trim();
		if (!couponFromQuery) return;
		if (autoCouponAppliedRef.current) return;
		autoCouponAppliedRef.current = true;
		setShowCouponInput(true);
		setCouponCode(couponFromQuery);
		handleApplyCoupon(couponFromQuery);
	}, [querySelection?.coupon, selectedPlanId, state.selectedPlanCbItem]);

	const handleRemoveCoupon = () => {
		setCouponCode('');
		setCouponStatus('');
		setPriceEstimate(null);
		sessionStorage.removeItem('appliedCouponCode');
	};

	const validatePaymentData = () => {
		const newErrors = {};

		if (!paymentData.cardholderName.trim()) {
			newErrors.cardholderName = 'Cardholder name is required';
		}

		const cardNum = paymentData.cardNumber.replace(/\s/g, '');
		const cardType = getCardType(cardNum);
		if (!isCardNumberValid(cardNum, cardType)) {
			newErrors.cardNumber =
				cardType === 'amex'
					? 'American Express card must be 15 digits and start with 34 or 37'
					: 'Invalid card number';
		}

		if (!paymentData.cardExpiry || paymentData.cardExpiry.length !== 5) {
			newErrors.cardExpiry = 'Invalid expiry date (MM/YY)';
		}

		const cvvPattern = cardType === 'amex' ? /^\d{4}$/ : /^\d{3}$/;
		if (!cvvPattern.test(paymentData.cardCvv || '')) {
			newErrors.cardCvv = cardType === 'amex' ? 'American Express requires a 4-digit CVV' : 'CVV must be 3 digits';
		}

		if (!paymentData.zipCode.trim()) {
			newErrors.zipCode = 'Zip/Postal code is required';
		} else if (!validateZipCode(paymentData.zipCode)) {
			// Provide country-specific error message
			const country = detectCountryFromZip(paymentData.zipCode);
			const countryNames = {
				US: 'US (5 digits)',
				AU: 'Australia (4 digits)',
				CA: 'Canada (postal code)',
				GB: 'UK (postal code)',
				NZ: 'New Zealand (4 digits)',
				IN: 'India (6 digits)'
			};
			newErrors.zipCode = `Invalid zip/postal code format for ${countryNames[country] || 'your country'}`;
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleCheckout = async () => {
		trackCheckoutPaymentEvent('card_submit_clicked', 'card');

		if (!validatePaymentData()) {
			trackCheckoutPaymentEvent('card_submit_validation_failed', 'card');
			Toast.error('Please fill all payment fields correctly');
			return;
		}

		setLoading(true);

		// Get user data from localStorage
		const userData = JSON.parse(localStorage.getItem('userData') || '{}');

		if (!userData.email) {
			trackCheckoutPaymentEvent('card_submit_missing_user_data', 'card');
			Toast.error('User data not found. Please start over.');
			setLoading(false);
				navigate(`/onboarding/create-profile${location.search || ''}`);
			return;
		}

		// Track checkout started
		window?.fbq?.('trackCustom', 'OnboardingV2_CheckoutStarted', {
			CustomEvent: 'OnboardingV2_CheckoutStarted',
			plan: selectedPlanId
		});

		try {
			// Parse expiry
			const [expMonth, expYear] = paymentData.cardExpiry.split('/');

			console.log('Creating subscription...');

			// Create subscription via API
			persistMetaCookiesToStorage();
			// Generate start trial event ID for CAPI deduplication
			const startTrialEventId = `start_trial_${userData.email}_${Date.now()}`;
			localStorage.setItem('start_trial_event_id', startTrialEventId);
			const trackingIds = getAllTrackingIds();
			const response = await onboardingV2Api.createSubscription({
				planId: state.selectedPlanCbItem || selectedPlanId,
				cardNumber: paymentData.cardNumber.replace(/\s/g, ''),
				cardExpMonth: expMonth,
				cardExpYear: expYear,
				cardCvv: paymentData.cardCvv,
				cardholderName: paymentData.cardholderName,
				billingZip: paymentData.zipCode,
				billingCountry: paymentData.country,
				couponId: couponStatus === 'valid' ? couponCode.trim() : undefined,
				...trackingIds
			});

			console.log('Subscription response:', response);
			trackCheckoutPaymentEvent('card_subscription_success', 'card', {
				subscriptionId: response.data?.subscription?.id
			});

			const onboardingPreferences = {
				...(state.onboardingPreferences || {})
			};

			try {
				await onboardingV2Api.updateOnboardingData({
					onboardingStep: 3,
					onboardingPreferences
				});
			} catch (err) {
				console.error('Failed to update onboarding data:', err);
			}

			// Track checkout completed
			window?.fbq?.('trackCustom', 'OnboardingV2_CheckoutCompleted', {
				CustomEvent: 'OnboardingV2_CheckoutCompleted'
			});

			// Update sessionStorage directly before navigation
			const updatedState = {
				...state,
				checkoutStatus: 'completed',
				currentStep: 3,
				onboardingPreferences
			};
			sessionStorage.setItem('onboarding_state', JSON.stringify(updatedState));

			// Also update the hook state
			updateState({
				checkoutStatus: 'completed',
				currentStep: 3,
				onboardingPreferences
			});

			// ✅ FIX: Update global userData with the returned userType from backend
			if (response.data?.userType) {
				// Get current user data from localStorage
				const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}');

				// Update with new userType and subscription info
				const updatedUserData = {
					...currentUserData,
					userType: response.data.userType,
					isTrialAvailed: true,
					requiredSeats: response.data.requiredSeats,
					availableSeats: response.data.availableSeats
				};

				// Update localStorage
				localStorage.setItem('userData', JSON.stringify(updatedUserData));

				// Update global atom state
				setUserData(prev => ({
					...prev,
					user: updatedUserData,
					userType: response.data.userType
				}));

				console.log('[Checkout] Updated userData with userType:', response.data.userType);
			}

			Toast.success('Subscription activated!');
			setLoading(false);

			console.log('Navigating to welcome...');
			// Include plan info in URL
			const params = new URLSearchParams({
				...Object.fromEntries(new URLSearchParams(location.search || '').entries()),
				plan: state.selectedPlanType || querySelection.planType || 'standard',
				billing: selectedBillingCycle,
				planId: selectedPlanId
			});
			navigate(`/onboarding/welcome?${params.toString()}`);
		} catch (error) {
			setLoading(false);
			const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Payment failed. Please try again.';
			trackCheckoutPaymentEvent('card_subscription_failed', 'card', {
				error: errorMsg
			});
			Toast.error(errorMsg);

			// Track error
			window?.fbq?.('trackCustom', 'OnboardingV2_CheckoutFailed', {
				CustomEvent: 'OnboardingV2_CheckoutFailed',
				error: errorMsg
			});
		}
	};

	return (
		<div className="onboarding-checkout">
			<div className="onboarding-checkout__grid-overlay" />

			<div className="onboarding-checkout__content">
				<header className="onboarding-checkout__header">
					<div className="onboarding-checkout__brand-row">
						<button
							type="button"
							className="onboarding-checkout__back-btn"
							onClick={() => {
								const userData = JSON.parse(localStorage.getItem('userData') || '{}');
								if (userData.email && location.state?.from === '/plans') {
									navigate('/plans');
								} else {
									navigate(-1);
								}
							}}
						>
							<KeyboardBackspaceOutlinedIcon />
						</button>
						<img src={Logo} alt="The Godfrey Method On Demand" className="onboarding-checkout__logo" />
					</div>
					<h1 className="onboarding-checkout__title">Checkout & Complete Sign Up</h1>
				</header>

				<section className="onboarding-checkout__layout">
					<aside className="onboarding-checkout__plan-card">
						<div className="onboarding-checkout__plan-top">
							<div>
								<div className="onboarding-checkout__plan-heading">
									{PlanIcon ? <PlanIcon fontSize="small" /> : null}
									<h2>{displayTitle}</h2>
								</div>
								<p className="onboarding-checkout__plan-price">
									${displayPrice}{!isUSD && <span className="onboarding-checkout__plan-price-currency"> USD</span>} <span>{selectedBillingCycle === 'yearly' ? 'per year' : 'per month'}</span>
								</p>
								{priceEstimate && discountAmount > 0 && (
									<div style={{marginTop: '8px', fontSize: '14px'}}>
										<p style={{color: '#666', textDecoration: 'line-through'}}>Original: ${totalPrice}{!isUSD && ' USD'}</p>
										<p style={{color: '#fff', fontWeight: '700', fontSize: '16px'}}>Final: ${displayPrice}{!isUSD && ' USD'}</p>
										<p style={{color: '#22c55e', fontWeight: '600'}}>Discount: -${discountAmount}{!isUSD && ' USD'}</p>
									</div>
								)}
								<button type="button" className="onboarding-checkout__selected-btn">
									Selected <DoneIcon fontSize="small" />
								</button>
							</div>
							<div className="onboarding-checkout__plan-perks">
								<h3>{activePlan.perks?.title || 'Includes'}</h3>
								<ul>
									{activePlan.perks?.features?.map(feature => (
										<li key={feature}>
											<DoneIcon fontSize="inherit" />
											<span>{feature}</span>
										</li>
									))}
								</ul>
							</div>
						</div>

						<button
							type="button"
							className="onboarding-checkout__change-plan"
							onClick={() => navigate(`/onboarding/select-plan${location.search || ''}`)}
						>
							Change plan
						</button>
					</aside>

					<div className="onboarding-checkout__payment-wrap">
						<div className="onboarding-checkout__form-grid">
							<div>
							<label className="onboarding-checkout__label" htmlFor="cardholderName">
								Cardholder Name
							</label>
							<div className="onboarding-checkout__input-with-icon">
								<PersonOutlineOutlinedIcon className="onboarding-checkout__input-icon" />
								<input
									id="cardholderName"
									className="onboarding-checkout__input onboarding-checkout__input--with-icon"
									type="text"
									placeholder="Jane Doe"
									value={paymentData.cardholderName}
									onChange={e => handleInputChange('cardholderName', e.target.value)}
									disabled={loading}
								/>
							</div>
							{errors.cardholderName && (
								<p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.cardholderName}</p>
							)}
							</div>
							<div className="onboarding-checkout__card-country-row">
								<div>
									<label className="onboarding-checkout__label" htmlFor="cardNumber">
										Card Number
									</label>
									<div className="onboarding-checkout__input-with-icon">
										<CreditCardOutlinedIcon className="onboarding-checkout__input-icon" />
										<input
											id="cardNumber"
											className="onboarding-checkout__input onboarding-checkout__input--with-icon"
											type="text"
											placeholder={detectedCardType === 'amex' ? 'XXXX XXXXXX XXXXX' : 'XXXX XXXX XXXX XXXX'}
											maxLength={detectedCardType === 'amex' ? 17 : 23}
											value={paymentData.cardNumber}
											onChange={e => handleInputChange('cardNumber', e.target.value)}
											disabled={loading}
										/>
									</div>
									{errors.cardNumber && (
										<p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.cardNumber}</p>
									)}
								</div>

								
								<div>
									<label className="onboarding-checkout__label" htmlFor="cardCvv">
										CVV
									</label>
									<div className="onboarding-checkout__input-with-icon">
										<AddCardOutlinedIcon className="onboarding-checkout__input-icon" />
										<input
											id="cardCvv"
											className="onboarding-checkout__input onboarding-checkout__input--with-icon"
											type="text"
											placeholder={detectedCardType === 'amex' ? '####' : '###'}
											maxLength={detectedCardType === 'amex' ? 4 : 3}
											value={paymentData.cardCvv}
											onChange={e => handleInputChange('cardCvv', e.target.value)}
											disabled={loading}
										/>
									</div>
									{errors.cardCvv && (
										<p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.cardCvv}</p>
									)}
								</div>
							</div>

							<div className="onboarding-checkout__small-row">
								<div>
									<label className="onboarding-checkout__label" htmlFor="cardExpiryDate">
										Expiry Date
									</label>
									<div className="onboarding-checkout__input-with-icon">
										<CalendarMonthOutlinedIcon className="onboarding-checkout__input-icon" />
										<input
											id="cardExpiryDate"
											className="onboarding-checkout__input onboarding-checkout__input--with-icon"
											type="text"
											placeholder="MM/YY"
											value={paymentData.cardExpiry}
											onChange={e => handleInputChange('cardExpiry', e.target.value)}
											disabled={loading}
										/>
									</div>
									{errors.cardExpiry && (
										<p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.cardExpiry}</p>
									)}
								</div>

								<div>
									<label className="onboarding-checkout__label" htmlFor="zipCode">
										Zip Code
									</label>
									<div className="onboarding-checkout__input-with-icon">
										<NearMeOutlinedIcon className="onboarding-checkout__input-icon" />
										<input
											id="zipCode"
											className="onboarding-checkout__input onboarding-checkout__input--with-icon"
											type="text"
											placeholder="#####"
											value={paymentData.zipCode}
											onChange={e => handleInputChange('zipCode', e.target.value)}
											disabled={loading}
										/>
									</div>
									{errors.zipCode && (
										<p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.zipCode}</p>
									)}
								</div>

								<div>
									<label className="onboarding-checkout__label" htmlFor="country">
										Country
									</label>
									<div className="onboarding-checkout__input-with-icon">
										<LanguageIcon className="onboarding-checkout__input-icon" />
										<select
											id="country"
											className="onboarding-checkout__input onboarding-checkout__input--with-icon"
											value={paymentData.country}
											onChange={e => handleInputChange('country', e.target.value)}
											disabled={loading}
										>
											{COUNTRIES.map(c => (
												<option key={c.code} value={c.code}>
													{c.name}
												</option>
											))}
										</select>
									</div>
									{errors.country && (
										<p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.country}</p>
									)}
								</div>
							</div>
						</div>

<div className="checkout-action-row">

  <button
    type="button"
    className="onboarding-checkout__submit"
    onClick={handleCheckout}
    disabled={loading}
  >
    {loading ? (
      'Processing...'
    ) : (
      <span>
        {plan.id.includes('STUDIO') ? 'Subscribe Now' : 'Start Free Trial'} <EastIcon />
      </span>
    )}
  </button>

  <span
    className="coupon-label"
    onClick={() => setShowCouponInput(!showCouponInput)}
  >
    Apply a Promo Code?
  </span>

  {showCouponInput && (
    <div className="coupon-input-wrapper">
      <input
        type="text"
        className="coupon-input"
        placeholder="Enter code"
        value={couponCode}
        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
        disabled={loading || couponStatus === 'validating' || couponStatus === 'valid'}
      />
      {couponStatus === 'valid' ? (
        <button
          type="button"
          className="coupon-btn coupon-btn--remove"
          onClick={handleRemoveCoupon}
          disabled={loading}
        >
          Remove
        </button>
      ) : (
        <button
          type="button"
          className="coupon-btn coupon-btn--apply"
					onClick={() => handleApplyCoupon()}
          disabled={loading || couponStatus === 'validating' || !couponCode.trim()}
        >
          {couponStatus === 'validating' ? <div className="coupon-spinner" /> : 'Apply'}
        </button>
      )}
    </div>
  )}

</div>

{!plan.id.includes('STUDIO') && (
  <p className="onboarding-checkout__trial-note">
    No charge today. Cancel anytime.
  </p>
)}

						<div className="onboarding-checkout__divider">
							<span className="onboarding-checkout__divider-desktop">Or Select a Payment Option Below</span>
							<span className="onboarding-checkout__divider-mobile">Pay with Credit Card</span>
						</div>

						<div className="onboarding-checkout__wallet-row">
							{applePayAvailable && (
								<div
									id="apple-pay-button-container"
									ref={applePayContainerRef}
									className="onboarding-checkout__wallet-btn onboarding-checkout__wallet-btn--apple"
								/>
							)}
							<div className="onboarding-checkout__wallet-btn-wrapper">
								{paypalLoading && (
									<button
										type="button"
										className="onboarding-checkout__wallet-btn onboarding-checkout__paypal-placeholder"
										disabled
										style={{
											cursor: 'not-allowed',
											opacity: 0.7,
											position: 'relative',
											maxWidth: '100%',
											borderRadius: '4px'
										}}
									>
										<div
											style={{
												position: 'absolute',
												top: '50%',
												left: '50%',
												transform: 'translate(-50%, -50%)',
												width: '15px',
												height: '15px',
												border: '3px solid #f3f3f3',
												borderTop: '3px solid #0070ba',
												borderRadius: '50%',
												animation: 'spin 1s linear infinite'
											}}
										></div>
										<img src={PayPalIcon} alt="PayPal" style={{ opacity: 0.3, height: '24px' }} />
									</button>
								)}
								<div id="paypal-container" style={{ display: paypalLoading ? 'none' : 'block' }}></div>
							</div>
						</div>
					</div>
				</section>
			</div>
		</div>
	);
};

export default Checkout;
